/* eslint-disable */

import { i18n } from '@/i18n'
import {
  CODE_VARIANTS_LENGTH,
  MOBILEREG,
  EMAILREG,
  URLREG,
  ANTHROPONYMREG,
} from '@m/validation/const'
// import { isValidPhoneNumber } from 'libphonenumber-js'

const { t: $t, d: $d, n: $n } = i18n.global

export function isNullOrUndefined(value: unknown): value is undefined | null {
  return value === null || value === undefined
}

export function isEmptyArray(arr: unknown): boolean {
  return Array.isArray(arr) && arr.length === 0
}

export function getSingleParam<TParam = unknown>(
  params: [TParam] | Record<string, TParam>,
  paramName: string
) {
  return Array.isArray(params) ? params[0] : params[paramName]
}

export function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined || value === '') {
    return true
  }

  if (Array.isArray(value) && value.length === 0) {
    return true
  }

  return false
}

function vin(this: any, value: any) {
  if (!value || !value.length) {
    return true
  }

  // const VIN_REG = /^(?=.*[0-9])(?=.*[A-z])[0-9A-z-]{17}$/
  const VIN_REG =
    /^(?<wmi>[A-HJ-NPR-Z\d]{3})(?<vds>[A-HJ-NPR-Z\d]{5})(?<check>[\dX])(?<vis>(?<year>[A-HJ-NPR-Z\d])(?<plant>[A-HJ-NPR-Z\d])(?<seq>[A-HJ-NPR-Z\d]{6}))$/
  if (!VIN_REG.test(value.trim())) {
    return this.config.globalProperties.$t('You need to enter a valid vin')
  }
  return true
}

function address(this: any, value: any) {
  if (
    isNullOrUndefined(value) ||
    isEmptyArray(value) ||
    value === false ||
    !value
  ) {
    return this.config.globalProperties.$t('This field is required')
  }
  if (!value?.address || !value.lat || !value.lng) {
    return this.config.globalProperties.$t('This field is required')
  }
  return true
}

function required(this: any, value: any) {
  if (
    isNullOrUndefined(value) ||
    isEmptyArray(value) ||
    value === false ||
    value === ''
  ) {
    return $t('This field is required')
  }
  return true
}

function anthroponym(this: any, value: any) {
  if (!value || !value.length) {
    return true
  }
  if (!ANTHROPONYMREG.test(value)) {
    return $t('Numbers and special characters except “-” are prohibited')
  }
  return true
}
function email(this: any, value: any) {
  if (!value || !value.length) {
    return true
  }
  if (!EMAILREG.test(value)) {
    return $t('This field must be a valid email')
  }
  return true
}

function confirmed(this: any, value: any, [target]: string, rest) {
  if (value === target) {
    return true
  }
  if ('newPassword1' === rest.field) {
    return $t('Password did not match')
  }
  return $t('Field did not match')
}

function min(
  this: any,
  value: unknown,
  params: [string | number] | { length: string | number }
): boolean | string {
  if (isEmpty(value)) {
    return true
  }

  const length = getSingleParam(params, 'length')
  if (Array.isArray(value)) {
    return value.every((val) => min(val, { length }))
  }

  if (String(value).length >= Number(length)) {
    return String(value).length >= Number(length)
  } else {
    return `${$t('Min')}: ${length}`
  }
}
const max = (
  value: unknown,
  params: [string | number] | { length: string | number }
): boolean | string => {
  if (isEmpty(value)) {
    return true
  }

  const length = getSingleParam(params, 'length')
  if (Array.isArray(value)) {
    return value.every((val) => max(val, { length }))
  }
  if (String(value).length <= Number(length)) {
    return String(value).length <= Number(length)
  } else {
    return `${$t('Max')}: ${length}`
  }
  // return [...String(value)].length <= Number(length)
}
function maxArrLength(
  this: any,
  value: unknown,
  params: [string | number] | { length: string | number }
): boolean | string {
  if (isEmpty(value)) {
    return true
  }

  const length = getSingleParam(params, 'length')
  if (Array.isArray(value)) {
    return value.length <= Number(length) || `${$t('Maximum')}: ${length}`
  } else {
    return `${$t('Maximum')}:${length}`
  }
}

function decimal(
  this: any,
  value: string,
  params: [string | number] | { length: string | number }
): boolean | string {
  const separator = '.'
  if (isEmpty(value)) {
    return true
  }
  const length = getSingleParam(params, 'length')
  const errorText = `${$t(
    'Ensure that the decimal places do not exceed'
  )} ${length}`
  if (isNullOrUndefined(value) || '' === value) {
    return errorText
  }

  if (Array.isArray(value)) {
    return value.every((val) => decimal(val, { length }))
  }

  // if is 0.
  if (0 === Number(length)) {
    return /^-?\d*$/.test(value) || errorText
  }
  const regexPart = '*' === length ? '+' : `{1,${length}}`
  const regex = new RegExp(
    `^[-+]?\\d*(\\${separator}\\d${regexPart})?([eE]{1}[-]?\\d+)?$`
  )

  if (!regex.test(value)) {
    return errorText
  }

  const parsedValue = parseFloat(value)

  // eslint-disable-next-line
  return parsedValue === parsedValue || errorText
}

function minArrLength(
  this: any,
  value: unknown,
  params: [string | number] | { length: string | number }
): boolean | string {
  const length = getSingleParam(params, 'length')
  if (isEmpty(value)) {
    return `${$t('Minimum')}: ${length}`
  }

  if (Array.isArray(value)) {
    return value.length >= Number(length) || `${$t('Minimum')}: ${length}`
  } else {
    return `${$t('Minimum')}: ${length}`
  }
}

function maxValue(
  this: any,
  value: unknown,
  params: [string | number] | { length: string | number }
): boolean | string {
  if (!value) {
    return true
  }
  const length = getSingleParam(params, 'length')
  if (Number(value) <= Number(length)) {
    return Number(value) <= Number(length)
  } else {
    return `${$t('Max')}: ${length}`
  }
}

function minValue(
  this: any,
  value: unknown,
  params: [string | number] | { length: string | number }
): boolean | string {
  if (!value) {
    return true
  }
  const length = getSingleParam(params, 'length')
  if (Number(value) >= Number(length)) {
    return Number(value) >= Number(length)
  } else {
    return `${$t('Min')}: ${length}`
  }
}

function maxTime(
  this: any,
  value: string,
  params: [string] | { length: string }
): boolean | string {
  const length = getSingleParam(params, 'length')
  if (!value || !length) {
    return true
  }
  return (
    Number(value.replace(':', '')) <= Number(length.replace(':', '')) ||
    `${$t('Time must be less than')}: ${length}`
  )
}

function minTime(
  this: any,
  value: string,
  params: [string] | { length: string }
): boolean | string {
  const length = getSingleParam(params, 'length')
  if (!value || !length) {
    return true
  }

  return (
    Number(value.replace(':', '')) >= Number(length.replace(':', '')) ||
    `${$t('Time must be greater than')}: ${length}`
  )
}

function url(this: any, value: any) {
  if (!value || !value.length) {
    return true
  }

  const URLREG =
    /^(http(s?):\/\/.)[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)$/
  if (!URLREG.test(value)) {
    return $t('This field must be a valid url')
  }
  return true
}
function multilangUrl(
  this: any,
  value: any,
  params: { lang: string }
) {
  const lang = getSingleParam(params, 'lang')
  return url(value[lang])
}

function postBackUrl(this: any, value: any) {
  if (!value || !value.length) {
    return true
  }

  if (!URLREG.test(value.replace('%7B', '{').replace('%7D', '}'))) {
    return $t('This field must be a valid url')
  }

  return true
}

function emailOrPhone(this: any, value: any) {
  return (
    EMAILREG.test(value) ||
    MOBILEREG.test(value) ||
    $t('Set correct email or phone')
  )
}

function numeric(this: any, value: any) {
  const ENUMBER = /^[0-9]+$/;

  return ENUMBER.test(value) || $t('May contain numeric characters only')
}

async function realPhone(this: any, value: any, [realValue]: string) {
  const { isValidPhoneNumber } = await import('libphonenumber-js')
  return isValidPhoneNumber(realValue) || $t('Phone is not valid')
}
async function phone(this: any, v: any, par) {
  const {
    lang,
    numberAfterCode,
    required,
    codeLength,
  } = par
  let value = v
  if (lang) {
    value = v[lang]
  }
  if (required && codeLength + Number(numberAfterCode) !== value?.length) {
    return $t('This field is required')
  }
  if (!value || !value.length) {
    return true
  }
  // if (value && value.length === codeLength) {
  //   return true
  // }
  // const regexp = [new RegExp(`^\\d{${numberAfterCode}}$`, 'img')]
  const codeLengthWithoutPlus = codeLength - 1
  const regexp = numberAfterCode
    .split('|')
    .map(
      (e) =>
        new RegExp(`^[\\+]\\d{${codeLengthWithoutPlus + Number(e)}}$`, 'i')
        // new RegExp(`^\\d{${Number(e)}}$`, 'i')
    )
  const result = regexp.some((t) => t.test(value))
  // console.log(result, regexp, value, result || $t('Phone is not valid'));
  return result || $t('Phone is not valid')
}

function requiredMultilang(this: any, value, { lang, code }: any, v) {
  if (code && code === value[lang].length) {
    return $t('This field is required')
  }
  if (!value || !value[lang]) {
    return $t('This field is required')
  }
  return true
}
function requiredMultilangs(this: any, value, { langs }: any) {
  let firstLangEmptyField
  const isHasEmptyField = langs.some((lang) => {
    if (!value[lang.id]) {
      firstLangEmptyField = lang.title
    }
    return !value[lang.id]
  })
  if (!value || isHasEmptyField) {
    return firstLangEmptyField + ': ' + $t('This field is required')
  }
  return true
}
function customUrlCheck(
  this: any,
  { service, link } = { service: null, link: null },
  params: { lang: string }
) {
  const lang = getSingleParam(params, 'lang')
  if ((service && !link[lang]) || (!service && link[lang])) {
    return $t('This field is required')
  }
  return url(link[lang])
}

function legalCode(this, value) {
  return (
    CODE_VARIANTS_LENGTH.find((variant) =>
      new RegExp(`^\\d{${variant}}$`).exec(value)
    ) || $t('Enter a valid code')
  )
}

export {
  url,
  multilangUrl,
  required,
  address,
  vin,
  email,
  confirmed,
  min,
  max,
  decimal,
  maxArrLength,
  minArrLength,
  maxValue,
  minValue,
  maxTime,
  minTime,
  postBackUrl,
  emailOrPhone,
  realPhone,
  phone,
  customUrlCheck,
  legalCode,
  requiredMultilang,
  requiredMultilangs,
  numeric,
  anthroponym,
}
